import * as React from 'react';

/**
 * Returns a callback `ref` to put on a "load more" sentinel inside a scrolling list. While [enabled],
 * [onLoadMore] fires whenever that element scrolls into view (or within [rootMargin] of it). Being a
 * callback ref, it re-binds the observer whenever the target moves to a different element — so the
 * sentinel can sit on a row partway down the list and follow it as more pages load in.
 *
 * Uses an `IntersectionObserver` with the default (viewport) root: the spec clips intersection by
 * any scrollable ancestor, so this works both when the list scrolls the page (mobile) and when it
 * scrolls inside its own container (the desktop table's ScrollArea).
 */
export function useInfiniteScroll<T extends HTMLElement>(
  onLoadMore: () => void,
  enabled: boolean,
  rootMargin = '200px',
): (node: T | null) => void {
  // Keep the latest callback without re-binding the observer on every render.
  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  return React.useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || !enabled || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) onLoadMoreRef.current();
        },
        { rootMargin },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [enabled, rootMargin],
  );
}
